


import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { HiMenu, HiArrowLeft, HiChevronLeft, HiChevronRight, HiChevronDown, HiPencil, HiTrash, HiSearch, HiUpload, HiInformationCircle } from 'react-icons/hi';
import audioIcon from '../../assets/icons/ic_audio.png';
import bookIcon from '../../assets/icons/ic_book.png';
import chatIcon from '../../assets/icons/ic_chat.png';
import videoIcon from '../../assets/icons/ic_video.png';
import cardSwipeIcon from '../../assets/icons/ic_card_swipe.png';
import storyIcon from '../../assets/icons/ic_story.png';
import mantraIcon from '../../assets/icons/ic_mantra.png';
import {
  fetchLessonList,
  createLesson,
  updateLesson,
  deleteLesson
} from '../../services/lessonContentService';
import { uploadImage } from '../../services/assetService';
import UploadCsvIcon from '../../assets/icons/upload-csv.svg';
import { uploadQuizCsv } from '../../services/quizService';
import Toast from '../../components/toaster';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { lessonCache } from './lessonCache';
import { invalidateLessonContentCache } from '../lessonContent/lessonContentCache';
import { bumpChapterUnitsCount } from '../chapters/chaptersCache';

const getUnitChildCount = (unit) => {
  if (!unit || typeof unit !== 'object') return null;

  const fromArray = (arr) => (Array.isArray(arr) ? arr.length : null);
  const fromNumber = (n) => (Number.isFinite(Number(n)) ? Number(n) : null);

  return (
    fromArray(unit.contents) ??
    fromArray(unit.lesson_contents) ??
    fromArray(unit.lessons) ??
    fromArray(unit.children) ??
    fromNumber(unit.contents_count) ??
    fromNumber(unit.content_count) ??
    fromNumber(unit.lessons_count) ??
    fromNumber(unit.lesson_count) ??
    fromNumber(unit.children_count) ??
    fromNumber(unit.child_count) ??
    null
  );
};

const Lesson = () => {
  const navigate = useNavigate();
  const { moduleId, chapterId } = useParams();
  const location = useLocation();
  const fromName = location.state?.fromName; // Chapter name (previous page card)
  const moduleName = location.state?.moduleName;

  // Debug: Log params when they change
  useEffect(() => {
    // console.log('Lesson page params:', { moduleId, chapterId });
  }, [moduleId, chapterId]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderLessons, setReorderLessons] = useState([]);
  const [reorderSaveLoading, setReorderSaveLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadResultModal, setShowUploadResultModal] = useState(false);
  const [uploadResult, setUploadResult] = useState({
    successful: [],
    skipped: [],
    error: [],
  });
  const [openUploadSection, setOpenUploadSection] = useState(null);
  const [editingUploadingImage, setEditingUploadingImage] = useState(false);
  const [editingUploadError, setEditingUploadError] = useState('');

  const [page, setPage] = useState(lessonCache.page || 1);
  const [limit] = useState(16);
  const [totalPages, setTotalPages] = useState(lessonCache.totalPages || 1);
  const [totalCount, setTotalCount] = useState(lessonCache.totalCount || 0);

  const [lessons, setLessons] = useState(lessonCache.chapterId === chapterId ? (lessonCache.lessons || []) : []);
  const [newLesson, setNewLesson] = useState({
    title: '',
    description: '',
    order: 1,
    content_type: 'audio',
    icon_type: 'Normal',
    image: null,
    imagePreview: null,
    imageSource: 'file',
    points: 0,
  });

  const [editingLesson, setEditingLesson] = useState({
    id: null,
    title: '',
    description: '',
    order: 1,
    content_type: 'audio',
    icon_type: 'Normal',
    image: null,
    imagePreview: null,
    imageSource: 'file',
    points: 0,
  });

  const [addErrors, setAddErrors] = useState({
    title: '',
    description: '',
    order: '',
    content_type: '',
    points: '',
  });

  const [editErrors, setEditErrors] = useState({
    title: '',
    description: '',
    points: '',
  });

  // Guard flags to prevent duplicate API calls
  const isLoadingRef = useRef(false);
  const lastCallParamsRef = useRef({ chapterId: null, page: null });

  // Debounce search query
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const searchDebounceTimerRef = useRef(null);

  const loadLessons = useCallback(async (currentSearch = debouncedSearch, forceRefresh = false, silent = false) => {
    if (!chapterId) {
      setLessons([]);
      if (!silent) setIsLoading(false);
      return;
    }

    const currentParams = { chapterId, page, search: currentSearch };
    const lastParams = lastCallParamsRef.current;

    const isFirstLoad = lessonCache.firstLoad || lessonCache.chapterId !== chapterId;
    const isSearchChange = currentSearch !== (lessonCache.lastSearch || '');
    const isPageChange = lastParams.page !== currentParams.page;

    // Prevent duplicate calls with same parameters
    const isSameParams = lastParams.chapterId === currentParams.chapterId &&
      lastParams.page === currentParams.page &&
      lastParams.search === currentParams.search;

    if (!forceRefresh && (isSameParams || isLoadingRef.current)) {
      return;
    }

    // Show loader on first load, search changes, and explicit page changes.
    if (!silent && (isFirstLoad || isSearchChange || isPageChange || forceRefresh)) {
      setIsLoading(true);
    }

    // Mark parameters as used and set loading
    lastCallParamsRef.current = currentParams;
    isLoadingRef.current = true;
    try {
      const response = await fetchLessonList(chapterId, page, limit, currentSearch);

      // Set loading to false immediately after API resolves
      if (!silent) setIsLoading(false);

      let fetchedLessons = [];
      let total = 0;
      let totalPagesData = 1;

      // Normalize response shapes (other pages like `module` handle multiple formats)
      // so pagination totals work consistently.
      if (response && Array.isArray(response.data)) {
        fetchedLessons = response.data;
        total =
          response.total ||
          response?.pagination?.totalCount ||
          response?.pagination?.total ||
          response.data.length;
        totalPagesData =
          response.totalPages ||
          response?.pagination?.totalPages ||
          Math.ceil((total || response.data.length) / limit);
      } else if (Array.isArray(response)) {
        fetchedLessons = response;
        total = response.length;
        totalPagesData = Math.ceil(response.length / limit);
      } else if (response && Array.isArray(response.lessons)) {
        fetchedLessons = response.lessons;
        total =
          response.total ||
          response?.pagination?.totalCount ||
          response?.pagination?.total ||
          response.lessons.length;
        totalPagesData =
          response.totalPages ||
          response?.pagination?.totalPages ||
          Math.ceil((total || response.lessons.length) / limit);
      } else if (response && Array.isArray(response.results)) {
        fetchedLessons = response.results;
        total =
          response.total ||
          response?.pagination?.totalCount ||
          response?.pagination?.total ||
          response.results.length;
        totalPagesData =
          response.totalPages ||
          response?.pagination?.totalPages ||
          Math.ceil((total || response.results.length) / limit);
      } else if (response && Array.isArray(response?.data?.data)) {
        fetchedLessons = response.data.data;
        total =
          response.total ||
          response?.pagination?.totalCount ||
          response?.pagination?.total ||
          response.data.data.length;
        totalPagesData =
          response.totalPages ||
          response?.pagination?.totalPages ||
          Math.ceil((total || response.data.data.length) / limit);
      } else if (response && (response.data || response?.data?.data)) {
        // Last-resort fallback for uncommon shapes.
        const possibleArray = response.data?.data ?? response.data?.lessons ?? response.data;
        if (Array.isArray(possibleArray)) {
          fetchedLessons = possibleArray;
          total =
            response.total ||
            response?.pagination?.totalCount ||
            response?.pagination?.total ||
            possibleArray.length;
          totalPagesData =
            response.totalPages ||
            response?.pagination?.totalPages ||
            Math.ceil((total || possibleArray.length) / limit);
        }
      }

      // Client-side filtering: Filter lessons by chapter_id as a safety measure
      // This ensures that even if the API returns all lessons, we only show lessons for this chapter
      const filteredLessons = fetchedLessons.filter((lesson) => {
        const lessonChapterId = lesson.chapter_id || lesson.chapterId || lesson.chapter?.chapter_id || lesson.chapter?.id;
        // Only include lessons that have a chapter_id AND it matches the selected chapter
        // Convert both to strings for comparison to handle number/string mismatches
        return lessonChapterId && String(lessonChapterId) === String(chapterId);
      });

      // If backend returns more than one page worth of data, slice client-side for the requested `page`.
      // This prevents "same items on page 2" when the API ignores pagination.
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedLessons = filteredLessons.length > limit
        ? filteredLessons.slice(startIndex, endIndex)
        : filteredLessons;

      // Set the lessons for the current page.
      setLessons(paginatedLessons);
      setTotalCount(total);
      setTotalPages(totalPagesData);

      lessonCache.lessons = paginatedLessons;
      lessonCache.chapterId = chapterId;
      lessonCache.page = page;
      lessonCache.totalPages = totalPagesData;
      lessonCache.totalCount = total;
      lessonCache.lastSearch = currentSearch;
      lessonCache.firstLoad = false;


    } catch (err) {

      setLessons([]);
      setTotalCount(0);
      setTotalPages(1);
      if (!silent) setIsLoading(false);
      lessonCache.firstLoad = false;
    } finally {

      if (!silent) setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [chapterId, page, limit, debouncedSearch]);


  useEffect(() => {

    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);

      setPage((currentPage) => currentPage !== 1 ? 1 : currentPage);
    }, 500); // 500ms debounce

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    // Only reuse cache when it's for the exact same chapter, page, and search term.
    // Otherwise page navigation can incorrectly show the same data.
    const hasCache = lessonCache.lessons !== null &&
      lessonCache.chapterId === chapterId &&
      lessonCache.page === page &&
      !lessonCache.firstLoad &&
      debouncedSearch === (lessonCache.lastSearch || '');

    if (hasCache && chapterId) {
      setLessons(lessonCache.lessons);
      setPage(lessonCache.page);
      setTotalPages(lessonCache.totalPages);
      setTotalCount(lessonCache.totalCount);
      lastCallParamsRef.current = { chapterId, page: lessonCache.page, search: debouncedSearch };

      // Background revalidate so counts update when navigating back from LessonContent.
      setTimeout(() => {
        loadLessons(debouncedSearch, true, true);
      }, 0);
      return;
    }

    // Reset state when chapterId changes to prevent data leakage
    setLessons([]);
    setPage(1);
    setTotalPages(1);
    setTotalCount(0);
    // Reset guard when chapterId changes (allows new calls for different chapterId)
    if (lastCallParamsRef.current.chapterId !== chapterId) {
      lastCallParamsRef.current = { chapterId: null, page: null, search: null };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId, page, debouncedSearch, loadLessons]);

  // Cleanup: Restore scrolling when modal closes
  useEffect(() => {
    if (!showReorderModal) {
      document.body.style.overflowX = '';
    }
    return () => {
      document.body.style.overflowX = '';
    };
  }, [showReorderModal]);

  // Close custom dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.custom-dropdown-container')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  useEffect(() => {
    if (chapterId) {
      loadLessons(debouncedSearch);
    }
  }, [chapterId, page, debouncedSearch, loadLessons]);

  const getPaginationRange = () => {
    if (totalPages === 0) return [];

    const totalPageNumbers = 5;
    const totalBlocks = totalPageNumbers + 2;

    if (totalPages <= totalBlocks) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    const leftSiblingIndex = Math.max(page - 1, 1);
    const rightSiblingIndex = Math.min(page + 1, totalPages);
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;
    const pages = [firstPageIndex];

    const startPage = shouldShowLeftDots ? leftSiblingIndex : 2;
    const endPage = shouldShowRightDots ? rightSiblingIndex : totalPages - 1;

    if (shouldShowLeftDots) {
      pages.push('dots');
    }

    for (let pageNum = startPage; pageNum <= endPage; pageNum += 1) {
      pages.push(pageNum);
    }

    if (shouldShowRightDots) {
      pages.push('dots');
    }

    pages.push(lastPageIndex);
    return pages;
  };

  // Memoized filtered lessons - ensure only lessons for current chapter are shown
  const filteredLessons = useMemo(() => {
    if (!chapterId || !lessons.length) {
      return [];
    }

    // Filter lessons to ensure they belong to the current chapter
    // Only show lessons that have a chapter_id AND it matches the selected chapter
    return lessons.filter((lesson) => {
      // Skip dummy data that doesn't have chapter_id
      if (lesson.id && lesson.id.toString().startsWith('dummy-')) {
        return false;
      }
      const lessonChapterId = lesson.chapter_id || lesson.chapterId || lesson.chapter?.chapter_id || lesson.chapter?.id;
      // Only include lessons that have a chapter_id AND it matches the selected chapter
      // Convert both to strings for comparison to handle number/string mismatches
      return lessonChapterId && String(lessonChapterId) === String(chapterId);
    });
  }, [lessons, chapterId]);

  const handleLessonClick = (lessonId, lessonTitle) => {
    // console.log('handleLessonClick called with:', { lessonId, moduleId, chapterId });
    if (!lessonId) {
      // console.error('Lesson ID is missing! Cannot navigate to lesson content page.');
      alert('Unit ID is missing. Please select a valid unit.');
      return;
    }
    if (moduleId && chapterId) {
      const path = `/module/${moduleId}/chapters/${chapterId}/lesson/${lessonId}`;
      // console.log('Navigating to:', path);
      navigate(path, {
        state: {
          fromName: lessonTitle,
          moduleName,
          chapterName: fromName,
          unitName: lessonTitle,
          thumbnailUrl: (() => {
            const unit = (Array.isArray(lessons) ? lessons : []).find(
              (row) => String(row?.id || row?._id) === String(lessonId)
            );
            return String(unit?.thumbnail_url || unit?.image || '').trim();
          })(),
        },
      });
    } else {
      // console.error('Module ID or Chapter ID is missing:', { moduleId, chapterId });
      alert('Module ID or Chapter ID is missing. Cannot navigate to lesson content.');
    }
  };

  const handleBack = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // console.log('handleBack called', { moduleId, chapterId });
    // Go back to the chapters list page for the current module
    if (moduleId) {
      const path = `/module/${moduleId}/chapters`;
      // console.log('Navigating to:', path);
      navigate(path, {
        state: {
          fromName: moduleName,
          moduleName,
        },
      });
    } else {
      // Fallback: use browser history or go to main module page
      // console.log('Module ID missing, using browser history');
      navigate(-1);
    }
  };

  const handleAddLesson = () => {
    setNewLesson({
      title: '',
      description: '',
      order: 1,
      content_type: 'audio',
      icon_type: '',
      image: null,
      imagePreview: null,
      imageSource: 'file',
      points: 0,
    });
    setAddErrors({
      title: '',
      description: '',
      order: '',
      content_type: '',
      points: '',
    });
    setUploadError('');
    setUploadingImage(false);
    setActiveDropdown(null);
    setShowAddModal(true);
  };

  const handleUploadCSV = async () => {
    if (!selectedFile) {
      Toast.error('Please select a JSON file');
      return;
    }
    if (!moduleId) {
      Toast.error('Module ID is missing. Cannot upload.');
      return;
    }
    setUploadingCsv(true);
    try {
      const res = await uploadQuizCsv(selectedFile, { module_id: moduleId, chapter_id: chapterId });
      const resultData = res?.data || {};
      setUploadResult({
        successful: resultData.successful || [],
        skipped: resultData.skipped || [],
        error: resultData.error || [],
      });
      setShowUploadModal(false);
      setSelectedFile(null);
      setShowUploadResultModal(true);

      lastCallParamsRef.current = { chapterId: null, page: null, search: null };
      isLoadingRef.current = false;
      lessonCache.firstLoad = true;
      lessonCache.lessons = null;
      invalidateLessonContentCache();
      await loadLessons(debouncedSearch);
    } catch (err) {
      Toast.error(err.message || 'Failed to upload JSON');
    } finally {
      setUploadingCsv(false);
    }
  };

  const handleImageChange = async (e, isEditing = false) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);

      if (isEditing) {
        setEditingUploadError('');
        setEditingUploadingImage(true);
        setEditingLesson({
          ...editingLesson,
          image: file,
          imagePreview: previewUrl,
        });

        try {
          const uploadedUrl = await uploadImage(file);
          setEditingLesson(prev => ({ ...prev, imagePreview: uploadedUrl }));
          setEditingUploadError('');
        } catch (error) {
          console.error('Image upload failed:', error);
          setEditingUploadError(error.message || 'Failed to upload image. Please try again.');
          setEditingLesson(prev => ({ ...prev, image: null, imagePreview: null }));
        } finally {
          setEditingUploadingImage(false);
        }
      } else {
        setUploadError('');
        setUploadingImage(true);
        setNewLesson({
          ...newLesson,
          image: file,
          imagePreview: previewUrl,
        });

        try {
          const uploadedUrl = await uploadImage(file);
          setNewLesson(prev => ({ ...prev, imagePreview: uploadedUrl }));
          setUploadError('');
        } catch (error) {
          console.error('Image upload failed:', error);
          setUploadError(error.message || 'Failed to upload image. Please try again.');
          setNewLesson(prev => ({ ...prev, image: null, imagePreview: null }));
        } finally {
          setUploadingImage(false);
        }
      }
    }
  };

  const handleSourceSwitch = (source, isEditing = false) => {
    if (isEditing) {
      setEditingLesson(prev => ({ ...prev, imageSource: source }));
    } else {
      setNewLesson(prev => ({ ...prev, imageSource: source }));
    }
  };

  const handleThumbnailUrlChange = (e, isEditing = false) => {
    const url = e.target.value;
    if (isEditing) {
      // Clean up previous object URL if it existed
      if (editingLesson.imagePreview && editingLesson.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(editingLesson.imagePreview);
      }
      setEditingLesson(prev => ({
        ...prev,
        thumbnail_url: url,
        image: null,
        imagePreview: url || null,
      }));
    } else {
      // Clean up previous object URL if it existed
      if (newLesson.imagePreview && newLesson.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(newLesson.imagePreview);
      }
      setNewLesson(prev => ({
        ...prev,
        thumbnail_url: url,
        image: null,
        imagePreview: url || null,
      }));
    }
  };

  const handleCreateLesson = async () => {
    // Validate required fields
    const errors = {};
    if (!newLesson.title.trim()) {
      errors.title = 'Title is required';
    }
    if (!newLesson.description.trim()) {
      errors.description = 'Description is required';
    }
    if (!newLesson.order || newLesson.order < 1) {
      errors.order = 'Order must be at least 1';
    }
    if (!newLesson.content_type) {
      errors.content_type = 'Content type is required';
    }
    const pointsValue = newLesson.points === '' || newLesson.points === null || newLesson.points === undefined
      ? null
      : parseInt(newLesson.points);

    if (pointsValue === null || isNaN(pointsValue)) {
      errors.points = 'points is required';
    } else if (pointsValue < 0) {
      errors.points = 'points must be at least 0';
    } else if (pointsValue > 999999) {
      errors.points = 'points cannot exceed 6 digits';
    }

    if (Object.keys(errors).length > 0) {
      setAddErrors(errors);
      return;
    }

    setSaving(true);
    try {
      // Use uploaded URL from imagePreview if available
      const thumbnailUrl = newLesson.imagePreview || '';

      const payload = {
        chapter_id: chapterId,
        title: newLesson.title.trim(),
        description: newLesson.description.trim(),
        order: parseInt(newLesson.order) || 1,
        thumbnail_url: thumbnailUrl,
        content_type: newLesson.content_type,
        icon_type: newLesson.icon_type === 'Normal' ? '' : (newLesson.icon_type || ''),
        points: parseInt(newLesson.points) || 0,
      };

      await createLesson(payload);
      // Optimistic: bump the chapter's unit count so Chapters page updates instantly on back navigation.
      bumpChapterUnitsCount(chapterId, 1);
      setShowAddModal(false);
      setActiveDropdown(null);
      // Reset guard flags to allow refresh
      lastCallParamsRef.current = { chapterId: null, page: null };
      isLoadingRef.current = false;
      await loadLessons();
    } catch (err) {
      alert(err.message || 'Failed to create lesson');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (lesson) => {
    // Handle both 'id' and '_id' (MongoDB uses '_id')
    const lessonId = lesson.id || lesson._id;
    // Handle both 'points' and 'points' (API might return either)
    const pointsValue = lesson.points !== undefined ? lesson.points : (lesson.points !== undefined ? lesson.points : 0);
    setEditingLesson({
      id: lessonId,
      title: lesson.title || lesson.lesson_name || '',
      description: lesson.description || '',
      order: lesson.order || 1,
      content_type: lesson.content_type || 'audio',
      icon_type: lesson.icon_type || lesson.iconType || 'Normal',
      image: null,
      imagePreview: lesson.thumbnail_url || null,
      imageSource: lesson.thumbnail_url ? 'url' : 'file',
      points: pointsValue,
    });
    setEditErrors({
      title: '',
      description: '',
      points: '',
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    // Field validations (match Add modal style with inline errors)
    const errors = {};
    if (!editingLesson.id) {
      errors.title = 'Unit ID is missing. Please try again.'; // show at top field to surface issue
    }
    if (!editingLesson.title?.trim()) {
      errors.title = 'Title is required';
    }
    if (!editingLesson.description?.trim()) {
      errors.description = 'Description is required';
    }
    const editpointsValue = editingLesson.points === '' || editingLesson.points === null || editingLesson.points === undefined
      ? null
      : parseInt(editingLesson.points);
    if (editpointsValue === null || isNaN(editpointsValue)) {
      errors.points = 'points is required';
    } else if (editpointsValue < 0) {
      errors.points = 'points must be at least 0';
    } else if (editpointsValue > 999999) {
      errors.points = 'points cannot exceed 6 digits';
    }
    if (Object.keys(errors).length > 0) {
      setEditErrors({
        title: errors.title || '',
        description: errors.description || '',
        points: errors.points || '',
      });
      return;
    }

    setSaving(true);
    try {
      // Use uploaded URL from imagePreview if available
      const thumbnailUrl = editingLesson.imagePreview || '';

      const payload = {
        lesson_id: editingLesson.id,
        title: editingLesson.title.trim(),
        description: editingLesson.description.trim(),
        order: parseInt(editingLesson.order) || 1,
        thumbnail_url: thumbnailUrl,
        content_type: editingLesson.content_type,
        icon_type: editingLesson.icon_type === 'Normal' ? '' : (editingLesson.icon_type || ''),
        points: parseInt(editingLesson.points) || 0,
      };

      await updateLesson(payload);
      setShowEditModal(false);
      // Reset guard flags to allow refresh
      lastCallParamsRef.current = { chapterId: null, page: null };
      isLoadingRef.current = false;
      await loadLessons();
    } catch (err) {
      // Surface API error non-intrusively; prefer Toast if available
      Toast.error(err.message || 'Failed to update lesson');
    } finally {
      setSaving(false);
    }
  };


  const handleSaveLessonOrder = async () => {
    // Validate chapterId is present
    if (!chapterId) {
      Toast.error('Chapter ID is missing. Cannot save lesson order.');
      return;
    }

    setReorderSaveLoading(true);
    try {
      const payload = reorderLessons.map((l, index) => ({
        lesson_id: String(l.id || l.lesson_id || l._id),
        order: index + 1,
      }));

      // Update each lesson's order via API
      const updatePromises = payload.map(async (item) => {
        const lesson = reorderLessons.find(
          (l) => String(l.id || l.lesson_id || l._id) === item.lesson_id
        );
        if (lesson) {
          const thumbnailUrl = (lesson.thumbnail_url && String(lesson.thumbnail_url).trim()) ||
            (lesson.image && String(lesson.image).trim()) ||
            '';

          return updateLesson({
            lesson_id: item.lesson_id,
            title: lesson.title || lesson.name,
            description: lesson.description || '',
            order: item.order,
            thumbnail_url: thumbnailUrl,
            content_type: lesson.content_type || 'audio',
            ...((lesson.icon_type || lesson.iconType) ? { icon_type: (lesson.icon_type || lesson.iconType) } : {}),
            points: lesson.points || lesson.points || 0,
          });
        }
      });

      await Promise.all(updatePromises);
      setShowReorderModal(false);
      Toast.success('Unit order updated successfully');
      // Reset guard flags to allow refresh
      lastCallParamsRef.current = { chapterId: null, page: null };
      isLoadingRef.current = false;
      await loadLessons();
    } catch (err) {
      console.error(err);
      Toast.error('Failed to save lesson order');
    } finally {
      setReorderSaveLoading(false);
    }
  };

  // Custom Grid Icon Component (3 rows, 2 dots each - vertical layout)
  const GridIcon = ({ size = 18 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-400"
    >
      {/* Left column - 3 dots */}
      <circle cx="4.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="15" r="1.5" fill="currentColor" />
      {/* Right column - 3 dots */}
      <circle cx="13.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="15" r="1.5" fill="currentColor" />
    </svg>
  );

  const SortableLessonItem = ({ lesson, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({
      id: lesson.id || lesson.lesson_id || lesson._id,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="flex items-center justify-between p-4 bg-[#FDF6EF] rounded-xl border border-white/10 cursor-grab active:cursor-grabbing touch-none"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="text-black flex items-center justify-center text-sm font-semibold min-w-[20px]">
            {index + 1}
          </div>

          {lesson.thumbnail_url || lesson.image ? (
            <img
              src={lesson.thumbnail_url || lesson.image}
              alt=""
              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-brand text-black flex items-center justify-center font-bold flex-shrink-0">
              {(lesson.title || lesson.name).charAt(0)}
            </div>
          )}

          <p
            className="text-black font-medium flex-1 leading-snug overflow-hidden"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {lesson.title || lesson.name}
          </p>

        </div>

        <div className="flex-shrink-0 ml-2">
          <GridIcon size={18} />
        </div>
      </div>
    );
  };

  const handleDelete = (lesson) => {
    setLessonToDelete(lesson);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!lessonToDelete) return;
    // Handle both 'id' and '_id' (MongoDB uses '_id')
    const lessonId = lessonToDelete.id || lessonToDelete._id;
    if (!lessonId) {
      alert('Unit ID is missing. Cannot delete Unit.');
      return;
    }
    setDeleting(true);
    try {
      await deleteLesson(lessonId);
      // Optimistic: decrement the chapter's unit count so Chapters page updates instantly on back navigation.
      bumpChapterUnitsCount(chapterId, -1);
      setShowDeleteModal(false);
      // Reset guard flags to allow refresh
      lastCallParamsRef.current = { chapterId: null, page: null };
      isLoadingRef.current = false;
      await loadLessons();
    } catch (err) {
      alert(err.message || 'Failed to delete Unit');
    } finally {
      setDeleting(false);
    }
  };


  const cancelDelete = () => {
    setShowDeleteModal(false);
    setLessonToDelete(null);
  };

  return (
    <div className="h-screen bg-primary flex bg-base-200 overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-[#FDF6EF] flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto no-scrollbar space-y-6">
          {/* Header */}
          <div className="mb-2 sm:mb-0 flex flex-col gap-3 md:flex-row md:items-center md:justify-between" style={{ position: 'relative', zIndex: 1000 }}>
            <div className="flex items-center gap-4 ml-1  sm:mt-0.5" style={{ position: 'relative', zIndex: 1001 }}>
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-1 btn btn-ghost btn-sm"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              {moduleId ? (
                <Link
                  to={`/module/${moduleId}/chapters`}
                  state={
                    moduleName
                      ? {
                        fromName: moduleName,
                        moduleName,
                      }
                      : undefined
                  }
                  className="p-2 rounded-lg hover:bg-white/50 transition-colors cursor-pointer inline-flex items-center justify-center"
                  style={{ position: 'relative', zIndex: 1000 }}
                  onClick={(e) => {
                    // console.log('Back link clicked - navigating to:', `/module/${moduleId}/chapters`);
                  }}
                >
                  <HiArrowLeft className="w-6 h-6 -ml-2 sm:-ml-1 sm:-mt-0.5 text-gray-800" />
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    // console.log('Back button clicked - no moduleId, going to /module');
                    navigate('/module');
                  }}
                  className="p-2 rounded-lg  hover:bg-white/50 transition-colors cursor-pointer"
                  style={{ position: 'relative', zIndex: 1000 }}
                >
                  <HiArrowLeft className="w-6 h-6  text-gray-800" />
                </button>
              )}
              <div className="flex flex-col md:flex-row md:items-baseline">
                <h1 className="text-xl md:text-3xl font-bold text-gray-800">
                  Units
                </h1>
                {fromName && (
                  <span className="text-sm md:text-lg font-semibold text-gray-600 mt-0.5 md:mt-0 md:ml-2">
                    {fromName}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="mb-5 md:mb-4 mt-4 md:mt-1">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3  mt-2 md:gap-4">
              {/* Search Input - Full width on mobile */}
              <div className="w-full md:w-40">
                <div className="relative">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 md:py-1.5 text-sm border border-[#ECECEC] focus:outline-none focus:border-gray-300 bg-white"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
              </div>

              {/* Action Buttons - Second line on mobile, same line on desktop */}
              <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto">
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="upload-quiz-btn flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                >
                  <HiUpload className="w-4 h-4 upload-quiz-icon-default" />
                  <img src={UploadCsvIcon} alt="Upload" className="w-5 h-5 upload-quiz-icon-mobile hidden" style={{ filter: 'brightness(0) invert(1)' }} />
                  <span className="upload-quiz-text">Upload File</span>
                </button>
                <button
                  onClick={() => {
                    if (!filteredLessons.length) return;
                    setReorderLessons(filteredLessons);
                    setShowReorderModal(true);
                  }}
                  disabled={!filteredLessons.length}
                  className={`flex items-center w-26 gap-2 px-3 py-2 text-sm font-semibold rounded-xl bg-brand text-white hover:shadow-lg shadow-md whitespace-nowrap flex-shrink-0 transition-all 
                 
                  }`}
                >
                  Edit Order
                </button>
                <button
                  onClick={handleAddLesson}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap"
                >
                  <span className="text-base">+</span>
                  <span>Add Unit</span>
                </button>
              </div>
            </div>
          </div>




          {/* {filteredLessons.length > 0 && (
                <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto">
                  <button
                    onClick={() => {
                      setReorderLessons(filteredLessons);
                      setShowReorderModal(true);
                    }}
                    className="flex items-center w-26 gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                  >
                    Edit Order
                  </button>
                  <button
                    onClick={handleAddLesson}
                    className="flex items-center gap-2 px-3 py-1 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90"
                  >
                    <span className="text-lg">+</span>
                    <span>Add Lesson</span>
                  </button>
                </div>
              )} */}

          {/* Lesson Cards */}
          <div>
            {isLoading ? (
              <div className="fixed inset-0 ml-0 lg:ml-64 z-40 flex items-center justify-center">
                <div className="flex items-center gap-3" role="status" aria-live="polite">
                  <svg
                    className="w-12 h-12 animate-spin"
                    viewBox="0 0 100 100"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#E9D5FF" />
                        <stop offset="25%" stopColor="#C084FC" />
                        <stop offset="50%" stopColor="#A855F7" />
                        <stop offset="75%" stopColor="#9333EA" />
                        <stop offset="100%" stopColor="#6B21A8" />
                      </linearGradient>
                    </defs>
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="url(#purple-gradient)"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray="60 200"
                      fill="none"
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                </div>
              </div>
            ) : filteredLessons.length === 0 ? (
              <div className="fixed inset-0 ml-0 lg:ml-64 z-30 flex items-center justify-center pointer-events-none">
                <div className="text-center space-y-3 pointer-events-auto">
                  {String(debouncedSearch || '').trim().length > 0 ? (
                    <>
                      <p className="text-sm text-gray-500">No units found. Try a different keyword.</p>
                    </>
                  ) : (
                    <>
                      <div className="mb-6">
                      <svg
                          className="w-24 h-24 mx-auto text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800">No Units added yet</h3>
                      <p className="text-sm text-gray-500">Start by adding the first unit for this chapter.</p>
                      <button
                        onClick={handleAddLesson}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 mx-auto"
                      >
                        <span className="text-lg">+</span>
                        <span>Add Unit</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 -mt-1 gap-4">
                {filteredLessons.map((lesson) => {
                  // Handle both 'id' and '_id' (MongoDB uses '_id')
                  const lessonId = lesson.id || lesson._id;
                  const childCount = getUnitChildCount(lesson);
                  return (
                    <div
                      key={lessonId}
                      onClick={() => handleLessonClick(lessonId, lesson.title || lesson.lesson_name || lesson.name || 'Untitled Lesson')}
                      className="bg-white rounded-xl p-4 sm:p-5 transition-shadow relative overflow-hidden hover:shadow-lg cursor-pointer border border-gray-200 min-h-[160px] flex flex-col"
                      style={{ boxShadow: '0px 3px 8px 0px #93939340' }}
                    >
                      {/* Title */}
                      <div className="flex items-center justify-between mb-3">
                        {/* <div className="mb-3"> */}
                        <h3 className="text-base font-semibold text-gray-800 line-clamp-2 overflow-hidden leading-snug">
                          {lesson.title || lesson.lesson_name || lesson.name || 'Untitled Lesson'}
                        </h3>

                        {/* Actions + Arrow */}
                        {/* Arrow */}
                        <HiArrowLeft className="w-4 h-4 rotate-180 text-gray-400 ml-1" />
                      </div>

                      <div className="mb-8 h-[2em]">
                        <p className="text-xs sm:text-sm text-gray-500 line-clamp-2  overflow-hidden"
                        >
                          {lesson.description || 'No description available'}
                        </p>
                      </div>

                      <div className="mt-auto flex items-end justify-between gap-2 flex-shrink-0">
                        {/* Bottom-left: Child count (always show a number) */}
                        <div className="flex items-center gap-2">
                          <div className="px-2 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 text-gray-700">
                            Lessons:{' '}
                            <span className="text-gray-900">{childCount ?? 0}</span>
                          </div>
                          {(() => {
                            const rawIconType = String(lesson.icon_type || lesson.iconType || '').trim().toLowerCase();
                            const iconMap = {
                              book: { src: bookIcon, label: 'Book' },
                              audio: { src: audioIcon, label: 'Audio' },
                              audiio: { src: audioIcon, label: 'Audio' },
                              video: { src: videoIcon, label: 'Video' },
                              cards: { src: storyIcon, label: 'Cards' },
                              card: { src: storyIcon, label: 'Cards' },
                              highlights: { src: cardSwipeIcon, label: 'Highlights' },
                              highlight: { src: cardSwipeIcon, label: 'Highlights' },
                              story: { src: storyIcon, label: 'Highlights' },
                              chat: { src: chatIcon, label: 'Chat' },
                              mantra: { src: mantraIcon, label: 'Mantra' },
                            };
                            const icon = iconMap[rawIconType];

                            if (!icon) return null;

                            return (
                              <div className="w-8 h-8 rounded-lg bg-[#EFEBF3] flex items-center justify-center p-1" title={icon.label}>
                                <img src={icon.src} alt={`${icon.label} icon`} className="w-5 h-5 object-contain" />
                              </div>
                            );
                          })()}
                        </div>

                        <div className="flex items-end justify-end gap-2">
                          {/* Edit */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(lesson);
                            }}
                            className="w-8 h-8 rounded-lg bg-[#EFEBF3] text-brand hover:opacity-90 flex items-center justify-center"
                            title="Edit"
                          >
                            <HiPencil className="w-4 h-4" />
                          </button>

                          {/* Delete */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(lesson);
                            }}
                            className="w-8 h-8 rounded-lg bg-[#FFF0F0] text-[#F76464] hover:opacity-90 flex items-center justify-center"
                            title="Delete"
                          >
                            <HiTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {/* Image Preview if available */}
                      {/* {(lesson.thumbnail_url || lesson.image) && (
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden mb-3">
                          <img 
                            src={lesson.thumbnail_url || lesson.image} 
                            alt={lesson.title || lesson.name} 
                            className="w-full h-full object-cover" 
                          />
                        </div>
                      )} */}

                      {/* Actions */}
                      {/* <div className="absolute bottom-3 right-3 flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(lesson);
                          }}
                          className="w-8 h-8 text-sm font-bold rounded-lg transition-colors hover:opacity-90 text-brand bg-[#EFEBF3] flex items-center justify-center"
                          title="Edit Lesson"
                        >
                          <HiPencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(lesson);
                          }}
                          className="w-8 h-8 font-bold text-sm rounded-lg transition-colors hover:opacity-90 text-[#F76464] bg-[#FFF0F0] flex items-center justify-center"
                          title="Delete Lesson"
                        >
                          <HiTrash className="w-4 h-4" />
                        </button>
                      </div> */}

                      <div className={`absolute -mr-4 bottom-0 right-0 ${lesson.thumbnail_url ? 'opacity-5' : 'opacity-15'} pointer-events-none`}>
                        <img
                          src={require('../../assets/round.png')}
                          alt="Round decoration"
                          className="w-24 h-24 sm:w-[120px] sm:h-[100px] object-contain"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {((totalPages > 1) || (totalPages === 1 && totalCount === 16)) && !isLoading && (
              <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    {getPaginationRange().map((pageNum, idx) =>
                      pageNum === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${pageNum === page
                            ? 'bg-brand border border-brand text-white shadow-sm hover:opacity-90'
                            : 'text-gray-900 hover:bg-gray-100'
                            }`}
                          aria-current={pageNum === page ? 'page' : undefined}
                        >
                          {pageNum}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Lesson Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col max-h-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Add New Unit</h2>
              {/* <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setShowAddModal(false);
                    setActiveDropdown(null);
                  }}
                >
                  ✕
                </button> */}
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 no-scrollbar">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newLesson.title}
                  onChange={(e) => {
                    setNewLesson({ ...newLesson, title: e.target.value });
                    if (addErrors.title) setAddErrors({ ...addErrors, title: '' });
                  }}
                  placeholder="Enter unit title"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${addErrors.title ? 'border-red-500' : 'border-gray-300'}`}
                />
                {addErrors.title && <p className="mt-1 text-xs text-red-500">{addErrors.title}</p>}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newLesson.description}
                  onChange={(e) => {
                    setNewLesson({ ...newLesson, description: e.target.value });
                    if (addErrors.description) setAddErrors({ ...addErrors, description: '' });
                  }}
                  placeholder="Enter unit description"
                  rows="1"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none -mb-1.5 resize-none ${addErrors.description ? 'border-red-500' : 'border-gray-300'}`}
                />
                {addErrors.description && <p className="mt-1 text-xs text-red-500">{addErrors.description}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    points <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="999999"
                    value={newLesson.points}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Prevent entering more than 6 digits (999999)
                      if (value && !isNaN(parseInt(value)) && parseInt(value) > 999999) {
                        setAddErrors({ ...addErrors, points: 'points cannot exceed 6 digits ' });
                        return;
                      }
                      // Prevent entering more than 6 digits by checking string length
                      if (value && value.length > 6) {
                        setAddErrors({ ...addErrors, points: 'points cannot exceed 6 digits' });
                        return;
                      }
                      setNewLesson({ ...newLesson, points: value });
                      if (addErrors.points) setAddErrors({ ...addErrors, points: '' });
                    }}
                    placeholder="Enter points"
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${addErrors.points ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {addErrors.points && (
                    <p className="mt-1 text-xs text-red-500">{addErrors.points}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Icon Type
                  </label>
                  <div className="relative custom-dropdown-container">
                    <button
                      type="button"
                      onClick={() => setActiveDropdown(activeDropdown === 'addIconType' ? null : 'addIconType')}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none flex items-center justify-between text-left bg-white"
                    >
                      <span className="truncate">{newLesson.icon_type || 'Normal'}</span>
                      <HiChevronDown className={`w-4 h-4 transition-transform duration-300 ease-in-out ${activeDropdown === 'addIconType' ? 'rotate-180' : ''}`} />
                    </button>
                    {activeDropdown === 'addIconType' && (
                      <div className="absolute z-[60] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-1">
                        {['Normal', 'Video', 'Audio', 'Cards', 'Highlights', 'Chat', 'Mantra'].map((type) => {
                          const normalizedSelectedType = String(newLesson.icon_type || '').trim().toLowerCase();
                          const isSelected = type === 'Normal'
                            ? normalizedSelectedType === '' || normalizedSelectedType === 'normal'
                            : (type.toLowerCase() === 'audio'
                              ? normalizedSelectedType === 'audio' || normalizedSelectedType === 'audiio'
                              : normalizedSelectedType === type.toLowerCase());

                          return (
                            <button
                              key={type}
                              type="button"
                              onClick={() => {
                                setNewLesson({ ...newLesson, icon_type: type });
                                setActiveDropdown(null);
                              }}
                              className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${isSelected ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'}`}
                            >
                              {type}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Thumbnail Image
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 group relative">
                      <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
                      <span className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[60] sm:whitespace-nowrap leading-tight inline-block ${newLesson.imageSource === 'url' ? 'w-[185px] sm:w-fit sm:max-w-[290px]' : 'w-[175px] sm:w-fit sm:max-w-[240px]'}`}>
                        {newLesson.imageSource === 'url'
                          ? 'Switch right to choose a file from your device'
                          : 'Switch left to paste an image URL.'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSourceSwitch(newLesson.imageSource === 'url' ? 'file' : 'url')}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${newLesson.imageSource === 'url' ? 'bg-brand' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${newLesson.imageSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  {newLesson.imageSource === 'url' ? (
                    <div>
                      <input
                        type="url"
                        value={newLesson.thumbnail_url}
                        onChange={(e) => handleThumbnailUrlChange(e, false)}
                        placeholder="Enter image URL"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                      />
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, false)}
                        disabled={uploadingImage}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none text-sm ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                      {uploadingImage && (
                        <div className="mt-2 flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-xs text-gray-600">Uploading image...</span>
                        </div>
                      )}
                      {uploadError && (
                        <p className="mt-1 text-xs text-red-500">{uploadError}</p>
                      )}
                    </div>
                  )}

               
                  {(newLesson.imagePreview || newLesson.thumbnail_url) && (
                    <div className="w-full h-36 rounded-lg overflow-hidden border border-gray-200 relative group">
                      <img
                        src={newLesson.imagePreview || newLesson.thumbnail_url}
                        alt="Thumbnail Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const errorDiv = e.target.nextElementSibling;
                          if (errorDiv) {
                            errorDiv.classList.remove('hidden');
                            errorDiv.classList.add('flex');
                          }
                        }}
                      />
                      <div className="hidden absolute inset-0 items-center justify-center bg-gray-100 rounded-lg">
                        <p className="text-xs text-gray-500">Failed to load image</p>
                      </div>

                     
                      <button
                        onClick={() =>
                          setNewLesson({
                            ...newLesson,
                            image: null,
                            imagePreview: null,
                            thumbnail_url: '',
                          })
                        }
                        className="absolute top-2 right-2 w-6 h-6 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div> */}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setShowAddModal(false);
                  setActiveDropdown(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-semibold text-white bg-brand rounded-lg hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2"
                onClick={handleCreateLesson}
                disabled={saving || uploadingImage}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Adding...
                  </>
                ) : (
                  'Create Unit'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Lesson Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">

            {/* Header */}
            <div className="px-6 py-4  border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-800">Edit Unit</h2>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4 ">

              {/* Title */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingLesson.title}
                  onChange={(e) =>
                    setEditingLesson({ ...editingLesson, title: e.target.value })
                  }
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${editErrors.title ? 'border-red-500' : 'border-gray-300'}`}
                />
                {editErrors.title && <p className="mt-1 text-xs text-red-500">{editErrors.title}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows="1"
                  value={editingLesson.description}
                  onChange={(e) =>
                    setEditingLesson({ ...editingLesson, description: e.target.value })
                  }
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none -mb-1.5 resize-none ${editErrors.description ? 'border-red-500' : 'border-gray-300'}`}
                />
                {editErrors.description && <p className="mt-1 text-xs text-red-500">{editErrors.description}</p>}
              </div>

              {/* points */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    points <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="999999"
                    value={editingLesson.points}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Prevent entering more than 6 digits (999999)
                      if (value && !isNaN(parseInt(value)) && parseInt(value) > 999999) {
                        setEditErrors({ points: 'points cannot exceed 6 digits ' });
                        return;
                      }
                      // Prevent entering more than 6 digits by checking string length
                      if (value && value.length > 6) {
                        setEditErrors({ points: 'points cannot exceed 6 digits ' });
                        return;
                      }
                      setEditingLesson({ ...editingLesson, points: value });
                      if (editErrors.points) setEditErrors({ points: '' });
                    }}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${editErrors.points ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {editErrors.points && (
                    <p className="mt-1 text-xs text-red-500">{editErrors.points}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Icon Type
                  </label>
                  <div className="relative custom-dropdown-container">
                    <button
                      type="button"
                      onClick={() => setActiveDropdown(activeDropdown === 'editIconType' ? null : 'editIconType')}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none flex items-center justify-between text-left bg-white"
                    >
                      <span className="truncate">{editingLesson.icon_type || 'Normal'}</span>
                      <HiChevronDown className={`w-4 h-4 transition-transform duration-300 ease-in-out ${activeDropdown === 'editIconType' ? 'rotate-180' : ''}`} />
                    </button>
                    {activeDropdown === 'editIconType' && (
                      <div className="absolute z-[60] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-1">
                        {['Normal', 'Video', 'Audio', 'Cards', 'Highlights', 'Chat', 'Mantra'].map((type) => {
                          const normalizedSelectedType = String(editingLesson.icon_type || '').trim().toLowerCase();
                          const isSelected = type === 'Normal'
                            ? normalizedSelectedType === '' || normalizedSelectedType === 'normal'
                            : (type.toLowerCase() === 'audio'
                              ? normalizedSelectedType === 'audio' || normalizedSelectedType === 'audiio'
                              : normalizedSelectedType === type.toLowerCase());

                          return (
                            <button
                              key={type}
                              type="button"
                              onClick={() => {
                                setEditingLesson({ ...editingLesson, icon_type: type });
                                setActiveDropdown(null);
                              }}
                              className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${isSelected ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'}`}
                            >
                              {type}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>


            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>

              <button
                onClick={handleEditSubmit}
                disabled={saving}
                className="px-4 py-2 text-sm font-semibold text-white bg-brand rounded-lg hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2"
              >  {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </>
              ) : (
                'Update'
              )}
              </button>
            </div>
          </div>
        </div>
      )}





      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6 md:p-10">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Delete Unit</h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Are you sure you want to delete this Unit?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg  transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>

        </div>
      )}

      {/* Upload JSON Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Upload Content via JSON</h2>
            </div>
            <div className="px-6 py-6 space-y-4">
              <p className="text-sm text-gray-600">
                Upload a JSON file containing your learning content. Ensure the file follows the required format for successful processing.
              </p>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-700">Select JSON File</label>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand/10 file:text-brand hover:file:bg-brand/20 cursor-pointer"
                />
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                }}
                disabled={uploadingCsv}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadCSV}
                disabled={uploadingCsv || !selectedFile}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {uploadingCsv ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Result Modal */}
      {showUploadResultModal && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">JSON Upload Summary</h2>
              <button onClick={() => setShowUploadResultModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="border rounded-xl overflow-hidden">
                <button onClick={() => setOpenUploadSection(openUploadSection === 'success' ? null : 'success')} className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 transition">
                  <span className="font-semibold text-green-700">Successfully Added ({uploadResult.successful.length})</span>
                  <HiChevronDown className={`w-5 h-5 text-green-700 ${openUploadSection === 'success' ? 'rotate-180' : ''}`} />
                </button>
                <div className={`px-4 bg-white overflow-hidden overflow-y-auto transition-all ${openUploadSection === 'success' && uploadResult.successful.length > 0 ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                  <ul className="text-sm text-green-700 space-y-2">
                    {uploadResult.successful.map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-semibold whitespace-nowrap">Row {item.row}:</span>
                        <span className="text-gray-700">
                          <div>module: {item.module}- {item.module_name}</div>
                          <div>Chapter: {item.chapter}- {item.chapter_name}</div>
                          <div>Lesson: {item.lesson}- {item.lesson_name}</div>
                          <div>Content: {item.content}- {item.content_name}</div>
                          <div>Question: {item.question}- {item.question_text}</div>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="border rounded-xl overflow-hidden">
                <button onClick={() => setOpenUploadSection(openUploadSection === 'skipped' ? null : 'skipped')} className="w-full flex items-center justify-between px-4 py-3 bg-yellow-50 hover:bg-yellow-100 transition">
                  <span className="font-semibold text-yellow-700">Skipped Records ({uploadResult.skipped.length})</span>
                  <HiChevronDown className={`w-5 h-5 text-yellow-700 ${openUploadSection === 'skipped' ? 'rotate-180' : ''}`} />
                </button>
                <div className={`px-4 bg-white overflow-hidden overflow-y-auto transition-all ${openUploadSection === 'skipped' && uploadResult.skipped.length > 0 ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                  <ul className="text-sm text-yellow-700 space-y-2">
                    {uploadResult.skipped.map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-semibold whitespace-nowrap">Row {item.row}:</span>
                        <span className="text-gray-700">
                          <div>module: {item.module}- {item.module_name}</div>
                          <div>Chapter: {item.chapter}- {item.chapter_name}</div>
                          <div>Lesson: {item.lesson}- {item.lesson_name}</div>
                          <div>Content: {item.content}- {item.content_name}</div>
                          <div>Question: {item.question}- {item.reason}</div>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {uploadResult.error.length > 0 && (
                <div className="border border-red-200 rounded-xl overflow-hidden bg-red-50">
                  <button onClick={() => setOpenUploadSection(openUploadSection === 'error' ? null : 'error')} className="w-full flex items-center justify-between px-4 py-3 hover:bg-red-100 transition">
                    <span className="font-semibold text-red-700">Errors ({uploadResult.error.length})</span>
                    <HiChevronDown className={`w-5 h-5 text-red-700 ${openUploadSection === 'error' ? 'rotate-180' : ''}`} />
                  </button>
                  <ul className={`px-4 text-sm text-red-700 space-y-2 overflow-hidden list-disc list-inside transition-all overflow-y-auto ${openUploadSection === 'error' ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                    {uploadResult.error.map((item, i) => (
                      <li key={i}>Row {item.row} — {item.error || item.reason || item.message || 'Unknown server error'}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button onClick={() => { setShowUploadResultModal(false); setOpenUploadSection(null); }} className="px-5 py-2 text-sm font-semibold text-white bg-brand rounded-lg hover:opacity-90">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Reorder Modal */}
      {showReorderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-[#ffffff] w-full max-w-lg rounded-2xl p-6 flex flex-col max-h-[90vh] overflow-hidden overflow-x-hidden">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-black/60 text-lg font-semibold">
                Reorder Units
              </h2>
              {/* <button
                onClick={() => setShowReorderModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button> */}
            </div>

            <p className="text-sm text-gray-400 mb-4 flex-shrink-0">
              Drag and drop units to reorder them. Click <b>Save Order</b> to apply.
            </p>

            {/* Drag List */}
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragStart={() => {
                // Prevent horizontal scrolling during drag
                document.body.style.overflowX = 'hidden';
              }}
              onDragEnd={(event) => {
                // Restore scrolling after drag
                document.body.style.overflowX = '';

                const { active, over } = event;
                if (!over || active.id === over.id) return;

                setReorderLessons((prev) => {
                  const oldIndex = prev.findIndex(
                    (l) => (l.id || l.lesson_id || l._id) === active.id
                  );
                  const newIndex = prev.findIndex(
                    (l) => (l.id || l.lesson_id || l._id) === over.id
                  );
                  return arrayMove(prev, oldIndex, newIndex);
                });
              }}
              onDragCancel={() => {
                // Restore scrolling if drag is cancelled
                document.body.style.overflowX = '';
              }}
            >
              <SortableContext
                items={reorderLessons.map(
                  (l) => l.id || l.lesson_id || l._id
                )}
                strategy={verticalListSortingStrategy}
              >
                {/* Calculate height: each item is ~72px (p-4 = 16px top/bottom + content ~40px) + gap-3 (12px) = ~84px per item, 5 items = ~420px */}
                <div className="space-y-3 overflow-y-auto overflow-x-hidden pr-2 flex-1 min-h-0" style={{ maxHeight: '420px' }}>
                  {reorderLessons.map((lesson, index) => (
                    <SortableLessonItem
                      key={lesson.id || lesson.lesson_id || lesson._id}
                      lesson={lesson}
                      index={index}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Footer */}
            <div className="flex justify-end gap-3 mt-6 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowReorderModal(false)}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveLessonOrder}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {reorderSaveLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lesson;
